import {Component, Input, OnInit, OnDestroy, AfterViewChecked, HostBinding, ChangeDetectionStrategy, ChangeDetectorRef, NgZone, ElementRef} from '@angular/core';
import {ProgressBarsService, SyncProgress, ImportProgress} from '../../services/progress-bars.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-progress-bars',
  templateUrl: './progress-bars.component.html',
  styleUrls: ['./progress-bars.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProgressBarsComponent implements OnInit, OnDestroy, AfterViewChecked {

  @Input() shopifySyncValue: number;
  @Input() shopifySyncCounter: number;
  @Input() shopifySyncPending: boolean;
  @Input() bulkSyncCounter: number;

  // Host binding to hide component when not active
  @HostBinding('class.inactive') get isInactive(): boolean {
    // Show if sync is active OR completed (waiting to hide) OR pending OR counter has value
    const shouldShow = this.isSyncActive ||
                      this.syncProgress.percentage === 100 ||
                      this.syncProgress.percentage > 0 ||
                      this.shopifySyncPending ||
                      this.shopifySyncCounter > 0 ||
                      this.bulkSyncCounter > 0 ||
                      this.syncProgress.totalItems > 0 ||
                      this.importProgress.isActive;

    return !shouldShow;
  }

  // Import progress
  importProgress: ImportProgress = {
    isActive: false,
    operation: '',
    userName: '',
    total: 0,
    completed: 0,
    percentage: 0,
  };

  // Collapsed/expanded state of the bottom dock. Collapsed is a single strip
  // line; expanded shows the full metrics panel. Persisted per user.
  public expanded: boolean = localStorage.getItem('progressDockExpanded') === 'true';

  public toggleExpanded(): void {
    this.expanded = !this.expanded;
    localStorage.setItem('progressDockExpanded', String(this.expanded));
    this.cdr.markForCheck();
  }

  // Which process feeds the collapsed strip: the sync takes precedence when
  // active, otherwise the import/push pipeline.
  public get stripIsSync(): boolean {
    return this.syncProgress.totalItems > 0 ||
           this.syncProgress.percentage > 0 ||
           this.shopifySyncCounter > 0 ||
           this.shopifySyncPending;
  }

  public get stripLabel(): string {
    if (this.stripIsSync) {
      return this.getSyncStatusText();
    }
    return this.importPhaseLabel;
  }

  // Human-readable label for the current import-pipeline phase. Used by the
  // collapsed strip (when the import drives it) and the expanded detail panel.
  public get importPhaseLabel(): string {
    switch (this.importProgress.phase) {
      case 'reading': return 'Reading the import file...';
      case 'analyzing': return 'Analyzing rows and resolving tags...';
      case 'saving': return 'Saving to database...';
      case 'confirming': return 'Confirming changes...';
      case 'confirm-saving': return 'Saving confirmed changes...';
      case 'pushing': return 'Pushing to production database...';
      case 'push-saving': return 'Writing production records...';
      case 'generating-feed': return 'Generating feed for Shopify sync...';
      default: return `${this.importProgress.userName || 'Import'} is ${this.importProgress.operation === 'create' ? 'creating' : 'updating'} products`;
    }
  }

  public get stripPercent(): number {
    return this.stripIsSync
      ? (this.syncProgress.percentage || this.shopifySyncValue || 0)
      : (this.importProgress.percentage || 0);
  }

  public get stripCompleted(): number {
    return this.stripIsSync ? this.syncProgress.completedItems : this.importProgress.completed;
  }

  public get stripTotal(): number {
    return this.stripIsSync ? this.syncProgress.totalItems : this.importProgress.total;
  }

  // Enhanced sync progress.
  // One aggregated line instead of one line per bulk operation — per-op lines
  // appeared and vanished every few seconds as operations cycled, constantly
  // changing the footer height and making the whole bar (and the grid it
  // pushes down) visibly bounce.
  public bulkStatusSummary: string = '';

  // Per-store bulk detail for the expanded panel — one stable line per store
  // (BHS / MWS, max 2 entries) so the panel height doesn't bounce as
  // operations cycle.
  public bulkStoreLines: string[] = [];

  // One stable description line for a single store's bulk operations.
  private describeStore(store: string, statuses: any[]): string {
    const ss = statuses.filter((s: any) => s.store === store);
    const running = ss.filter((s: any) => s.state === 'running');
    const uploading = ss.filter((s: any) => s.state === 'submitting' || s.state === 'preparing').length;
    const waiting = ss.filter((s: any) => s.state === 'waiting-shop').length;
    const bits: string[] = [];
    if (running.length) {
      const objects = running.reduce((acc: number, s: any) => acc + (s.objectCount || 0), 0);
      bits.push(`${running.length} operation${running.length > 1 ? 's' : ''} running — ${objects.toLocaleString()} processed by Shopify`);
    }
    if (uploading) { bits.push(`${uploading} uploading`); }
    if (waiting) { bits.push(`${waiting} waiting for the shop`); }
    return bits.length ? `${store}: ${bits.join(', ')}` : '';
  }

  private buildBulkSummary(statuses: any[]): string {
    if (!statuses.length) { return ''; }
    const stores = [...new Set(statuses.map((s: any) => s.store))];
    const parts = stores.map(store => this.describeStore(store, statuses)).filter(Boolean);
    return parts.length ? `Bulk sync — ${parts.join(' · ')}` : '';
  }

  private buildBulkStoreLines(statuses: any[]): string[] {
    if (!statuses.length) { return []; }
    const stores = [...new Set(statuses.map((s: any) => s.store))].slice(0, 2);
    return stores.map(store => this.describeStore(store, statuses)).filter(Boolean);
  }

  syncProgress: SyncProgress = {
    totalItems: 0,
    completedItems: 0,
    percentage: 0,
    startTime: null,
    estimatedTimeRemaining: '',
    syncStatus: '',
    itemsPerSecond: 0
  };

  // Professional enhancements
  isCounterUpdating: boolean = false;
  hasSyncError: boolean = false;
  isPaused: boolean = false;
  private lastCompletedItems: number = 0;
  private averageSpeed: number = 0;
  private speedHistory: number[] = [];
  private successfulItems: number = 0;
  private lastSpeedUpdate: Date = new Date();
  private elapsedStartTime: Date | null = null;
  private hideTimeout: any = null;
  private tickInterval: any = null;

  // For cleanup
  private destroy$ = new Subject<void>();

  private lastSyncBarOffset: number = 0;
  private isHiding: boolean = false;

  constructor(
    private progressBarsService: ProgressBarsService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private el: ElementRef
  ) {
    this.shopifySyncValue = 0;
    this.shopifySyncCounter = 0;
    this.bulkSyncCounter = 0;
    this.shopifySyncPending = false;
  }

  ngAfterViewChecked(): void {
    // Measure actual rendered height and set CSS variable so the grid can shrink
    const height = this.isInactive ? 0 : this.el.nativeElement.offsetHeight;
    if (height !== this.lastSyncBarOffset) {
      this.lastSyncBarOffset = height;
      document.documentElement.style.setProperty('--sync-bar-offset', height + 'px');
    }
  }

  ngOnInit(): void {
    // Run subscriptions outside Angular's zone to prevent unwanted change detection
    this.ngZone.runOutsideAngular(() => {
      // Legacy subscriptions for backward compatibility
      // Use markForCheck() instead of detectChanges() to prevent focus stealing
      this.progressBarsService.shopifySyncValue?.subscribe((value: number) => {
        this.shopifySyncValue = +value;
        this.cdr.markForCheck();
      });
      this.progressBarsService.shopifySyncPendingStatus?.subscribe((value: boolean) => {
        this.shopifySyncPending = value;
        this.cdr.markForCheck();
      });
      this.progressBarsService.shopifySyncCounter?.subscribe((value: number) => {
        this.shopifySyncCounter = +value;
        this.cdr.markForCheck();
      });
      this.progressBarsService.bulkValue?.subscribe((value: number) => {
        this.bulkSyncCounter = value;
        this.cdr.markForCheck();
      });

      // Import progress subscription
      this.progressBarsService.importProgress$
        .pipe(takeUntil(this.destroy$))
        .subscribe(progress => {
          this.importProgress = progress;
          this.cdr.markForCheck();
        });

      // Bulk operation status lines (per store)
      this.progressBarsService.bulkSyncStatus$
        .pipe(takeUntil(this.destroy$))
        .subscribe(statusMap => {
          const statuses = Object.values(statusMap || {});
          this.bulkStatusSummary = this.buildBulkSummary(statuses);
          this.bulkStoreLines = this.buildBulkStoreLines(statuses);
          this.cdr.markForCheck();
        });

      // Enhanced sync progress subscription
      this.progressBarsService.syncProgress$
        .pipe(takeUntil(this.destroy$))
        .subscribe(progress => {
          // Start 1-second tick interval for elapsed/ETA while sync is active
          if (!this.tickInterval && progress.totalItems > 0 && progress.percentage < 100) {
            this.tickInterval = setInterval(() => this.cdr.markForCheck(), 1000);
          }
          // Clear tick interval when sync completes or resets
          if (this.tickInterval && (progress.percentage >= 100 || progress.totalItems === 0)) {
            clearInterval(this.tickInterval);
            this.tickInterval = null;
          }

          // Track counter updates for animation
          if (progress.completedItems !== this.lastCompletedItems) {
            this.isCounterUpdating = true;
            setTimeout(() => {
              this.isCounterUpdating = false;
              this.cdr.markForCheck();
            }, 200);
            this.lastCompletedItems = progress.completedItems;
          }

          // Update speed history
          if (progress.itemsPerSecond > 0) {
            this.speedHistory.push(progress.itemsPerSecond);
            if (this.speedHistory.length > 10) {
              this.speedHistory.shift();
            }
            this.averageSpeed = this.speedHistory.reduce((a, b) => a + b, 0) / this.speedHistory.length;
          }

          // Update elapsed time tracking
          this.updateElapsedTime(progress);

          // Check for errors (you can enhance this based on your actual error tracking)
          // This is a placeholder - you should check actual error state from your service
          this.hasSyncError = false; // Set this based on actual error state

          // Handle sync completion
          if (progress.percentage === 100 && this.syncProgress.percentage !== 100) {
            this.handleSyncComplete();
          } else if (progress.percentage < 100 && this.hideTimeout) {
            // Only cancel hide for a genuine new sync (totalItems changed),
            // not for stale counter events during the hide window
            if (!this.isHiding) {
              clearTimeout(this.hideTimeout);
              this.hideTimeout = null;
            }
          }

          this.syncProgress = progress;

          // Use markForCheck to schedule change detection without stealing focus
          this.cdr.markForCheck();
        });
    });
  }

  ngOnDestroy(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    document.documentElement.style.setProperty('--sync-bar-offset', '0px');
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Helper method to check if sync is active
  get isSyncActive(): boolean {
    // Only use the enhanced sync progress for determining active state
    // This prevents conflicts between legacy and new tracking
    return this.syncProgress.totalItems > 0 && this.syncProgress.percentage < 100;
  }

  // Handle sync completion - hide after brief delay
  private handleSyncComplete(): void {
    this.isHiding = true;

    // Clear any existing timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }

    // Hide the progress bar after showing complete state for 3 seconds
    this.hideTimeout = setTimeout(() => {
      this.hideProgressBar();
    }, 3000); // Show complete state for 3 seconds
  }

  // Method to cleanly hide the progress bar
  private hideProgressBar(): void {
    this.isHiding = false;

    // Reset the sync progress to hide the bar completely
    this.syncProgress = {
      totalItems: 0,
      completedItems: 0,
      percentage: 0,
      startTime: null,
      estimatedTimeRemaining: '',
      syncStatus: '',
      itemsPerSecond: 0
    };
    this.shopifySyncCounter = 0;
    this.shopifySyncPending = false;
    this.elapsedStartTime = null;
    this.speedHistory = [];
    this.averageSpeed = 0;
    this.hideTimeout = null;
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  }

  // Helper method to format the counter display
  get formattedCounter(): string {
    if (this.syncProgress.totalItems > 0) {
      return `${this.syncProgress.completedItems}/${this.syncProgress.totalItems}`;
    }
    return '';
  }

  // Helper method to get remaining items count
  get remainingItems(): number {
    if (this.syncProgress.totalItems > 0) {
      return this.syncProgress.totalItems - this.syncProgress.completedItems;
    }
    return 0;
  }

  // Helper method to format the full counter display with remaining
  get formattedCounterWithRemaining(): string {
    if (this.syncProgress.totalItems > 0) {
      const remaining = this.remainingItems;
      return `${this.syncProgress.completedItems}/${this.syncProgress.totalItems} (${remaining} remaining)`;
    }
    return '';
  }

  // Helper method to get progress bar value
  get progressBarValue(): number {
    return this.syncProgress.percentage || this.shopifySyncValue || 0;
  }

  // Elapsed time display — only when we have a real startTime (not mid-sync baseline)
  get elapsedDisplay(): string {
    if (!this.syncProgress.startTime) return '';
    const elapsed = this.getElapsedTime();
    return elapsed === '0s' ? '' : elapsed;
  }

  // Items per minute — more intuitive for users than items/sec
  get itemsPerMinute(): number {
    if (this.syncProgress.itemsPerSecond <= 0) return 0;
    return Math.round(this.syncProgress.itemsPerSecond * 60);
  }

  // Estimated wall-clock completion time (e.g. "3:45 PM")
  get estimatedCompletionTime(): string {
    if (!this.syncProgress.estimatedTimeRemaining || this.syncProgress.percentage >= 100) return '';
    const remaining = this.syncProgress.itemsPerSecond > 0
      ? this.remainingItems / this.syncProgress.itemsPerSecond
      : 0;
    if (remaining <= 0 || !isFinite(remaining)) return '';
    const done = new Date(Date.now() + remaining * 1000);
    const hours = done.getHours();
    const minutes = done.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    const m = minutes < 10 ? '0' + minutes : '' + minutes;
    return `${h}:${m} ${ampm}`;
  }

  // Professional Enhancement Methods

  // Get appropriate Material Icon based on sync state
  getSyncIcon(): string {
    if (this.hasSyncError) {
      return 'error_outline';
    }

    if (this.isPaused) {
      return 'pause_circle_filled';
    }

    if (this.syncProgress.percentage === 100) {
      return 'check_circle';
    }

    if (this.syncProgress.percentage > 0) {
      return 'sync';
    }

    if (this.shopifySyncPending) {
      return 'hourglass_empty';
    }

    return 'radio_button_unchecked';
  }

  // Get descriptive status text based on progress
  getSyncStatusText(): string {
    const p = this.syncProgress.percentage;

    // Handle error state
    if (this.hasSyncError) {
      return 'Error';
    }

    // Handle paused state
    if (this.isPaused) {
      return 'Paused';
    }

    // Use provided status if available
    if (this.syncProgress.syncStatus) {
      return this.syncProgress.syncStatus;
    }

    // Status based on progress: "Starting..." only until the first item
    // completes, then "Syncing..." for the remainder of the run.
    if (p === 100) return 'Complete';
    if (this.syncProgress.completedItems > 0) return 'Syncing...';
    if (p > 0) return 'Starting...';

    // Pending state
    if (this.shopifySyncPending) {
      return 'Waiting';
    }

    return 'Ready';
  }

  // Tooltip Methods for Enhanced UX

  // Get status tooltip with detailed information
  getStatusTooltip(): string {
    const p = this.syncProgress.percentage;

    if (this.hasSyncError) {
      return 'Sync encountered errors. Click for details.';
    }

    if (p === 100) {
      return 'Sync completed successfully!';
    }

    if (p >= 75) {
      return 'Finalizing sync operations...';
    }

    if (p >= 50) {
      return 'Processing items and updating Shopify...';
    }

    if (p >= 25) {
      return 'Establishing connection and starting sync...';
    }

    if (p > 0) {
      return 'Initializing sync process...';
    }

    if (this.shopifySyncPending) {
      return 'Waiting for sync to start...';
    }

    return 'Sync status';
  }

  // Get progress bar tooltip with time and speed info
  getProgressTooltip(): string {
    if (this.syncProgress.percentage === 100) {
      return 'Sync completed successfully!';
    }

    if (this.hasSyncError) {
      return 'Sync has errors - click for details';
    }

    if (this.isPaused) {
      return 'Sync is paused - click to resume';
    }

    const parts: string[] = [];

    // Add progress percentage
    if (this.syncProgress.percentage > 0) {
      parts.push(`Progress: ${this.syncProgress.percentage}%`);
    }

    // Add items processed and remaining
    if (this.syncProgress.totalItems > 0) {
      parts.push(`Items: ${this.syncProgress.completedItems} of ${this.syncProgress.totalItems}`);
      parts.push(`Remaining: ${this.remainingItems}`);
    }

    // Add elapsed time
    if (this.elapsedStartTime) {
      const elapsed = this.getElapsedTime();
      parts.push(`Elapsed: ${elapsed}`);
    }

    // Add estimated time
    if (this.syncProgress.estimatedTimeRemaining) {
      parts.push(`Remaining: ${this.syncProgress.estimatedTimeRemaining}`);
    }

    // Add speed
    if (this.syncProgress.itemsPerSecond > 0) {
      parts.push(`Speed: ${this.syncProgress.itemsPerSecond} items/sec`);
    }

    return parts.join(' | ');
  }

  // Calculate elapsed time
  private getElapsedTime(): string {
    if (!this.elapsedStartTime) {
      return '0s';
    }

    const now = new Date();
    const elapsed = Math.floor((now.getTime() - this.elapsedStartTime.getTime()) / 1000);

    if (elapsed < 60) {
      return `${elapsed}s`;
    } else if (elapsed < 3600) {
      const minutes = Math.floor(elapsed / 60);
      const seconds = elapsed % 60;
      return `${minutes}m ${seconds}s`;
    } else {
      const hours = Math.floor(elapsed / 3600);
      const minutes = Math.floor((elapsed % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  // Update elapsed start time when sync starts
  private updateElapsedTime(progress: SyncProgress): void {
    if (progress.startTime && !this.elapsedStartTime) {
      this.elapsedStartTime = new Date(progress.startTime);
    }

    if (progress.percentage === 100 || progress.totalItems === 0) {
      this.elapsedStartTime = null;
    }
  }

}
